import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Badge, Btn, Empty, Field, PanelHead } from "./common";
import { CalendarConnectionCard } from "./CalendarConnectionCard";
import { ReviewConfirm } from "./ReviewConfirm";
import { Plus, Trash2 } from "lucide-react";

const fmt = (iso, allDay) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return allDay
      ? d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric", timeZone: "Europe/London" })
      : d.toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });
  } catch { return iso; }
};
const newKey = () => `idem_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

/* Real Google Calendar: upcoming events (read) + event drafts that are only written after an explicit
   on-screen review/confirmation (server-side one-time token + idempotency key). */
export const CalendarPanelReal = () => {
  const { tx } = useI18n();
  const [st, setSt] = useState(null);
  const [events, setEvents] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [form, setForm] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDrafts = useCallback(async () => {
    try { const r = await api.get("/calendar/drafts"); setDrafts(r.data.drafts || []); } catch { /* ignore */ }
  }, []);
  const loadEvents = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/calendar/upcoming", { params: { days: 14, limit: 15 } }); setEvents(r.data); }
    catch (e) { setEvents(null); toast.error(e?.response?.data?.detail || tx("Takvim okunamadı")); }
    finally { setLoading(false); }
  }, [tx]);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("gmail");
    if (p === "connected") toast.success(tx("Google yetkilendirmesi güncellendi."));
  }, [tx]);

  const saveDraft = async () => {
    try {
      const payload = { ...form, attendees: (form.attendeesText || "").split(",").map((x) => x.trim()).filter(Boolean) };
      delete payload.attendeesText;
      if (form.draft_id) await api.put(`/calendar/drafts/${form.draft_id}`, payload);
      else await api.post("/calendar/drafts", payload);
      toast.success(form.draft_id ? tx("Taslak güncellendi — önceki onay geçersiz") : tx("Etkinlik taslağı kaydedildi"));
      setForm(null); loadDrafts();
    } catch (e) { toast.error(e?.response?.data?.detail || tx("Taslak kaydedilemedi")); }
  };
  const openReview = async (d) => {
    try {
      const r = await api.post(`/calendar/drafts/${d.draft_id}/confirm`);
      setReview({ ...r.data, draft_id: d.draft_id, idem: newKey() });
    } catch (e) { toast.error(e?.response?.data?.detail || tx("Onay alınamadı")); }
  };
  const apply = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const r = await api.post(`/calendar/drafts/${review.draft_id}/apply`,
        { confirm_token: review.confirm_token, idempotency_key: review.idem });
      toast.success(r.data.duplicate_suppressed
        ? tx("Bu işlem daha önce uygulanmış — ikinci etkinlik oluşturulmadı")
        : tx("Takvime işlendi (etkinlik: {id})", { id: r.data.event_id }));
      setReview(null); loadDrafts(); loadEvents();
    } catch (e) { toast.error(e?.response?.data?.detail || tx("Takvim güncellenemedi")); setReview(null); }
    finally { setLoading(false); }
  };
  const del = async (id) => { await api.delete(`/calendar/drafts/${id}`); loadDrafts(); };

  const canWrite = !!st?.can_write_events;
  const dupGroups = React.useMemo(() => {
    const map = {};
    (events?.events || []).forEach((e) => {
      const k = `${(e.title || "").trim().toLowerCase()}|${e.start}`;
      (map[k] = map[k] || []).push(e);
    });
    return Object.values(map).filter((g) => g.length > 1);
  }, [events]);
  return (
    <div data-testid="panel-calendar">
      <PanelHead title={tx("Takvim")} sub={tx("Google Takvim — okuma ve onaylı düzenleme (Europe/London)")} />

      <div className="panel p-4 sm:p-5 mb-4">
        <CalendarConnectionCard onChanged={(s) => { setSt(s); if (s?.can_write_events && events === null && !loading) loadEvents(); }} />
      </div>

      {canWrite && (
        <div className="panel p-4 sm:p-5 mb-4" data-testid="calendar-upcoming">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="label-eyebrow">{tx("Yaklaşan etkinlikler (14 gün)")}</div>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={loadEvents} disabled={loading} data-testid="calendar-reload-btn">
                {loading ? tx("Yükleniyor…") : tx("Yenile")}
              </Btn>
              <Btn onClick={() => setForm({ title: "", date: "", start_time: "", end_time: "", all_day: false, attendeesText: "", notify: false, location: "", description: "" })}
                data-testid="calendar-new-draft-btn"><Plus size={13} className="inline mr-1" />{tx("Yeni etkinlik")}</Btn>
            </div>
          </div>
          {events && events.count === 0 && <Empty text={tx("Bu aralıkta randevu yok.")} />}
          {dupGroups.length > 0 && (
            <div className="mb-3 rounded-lg border border-amber-400/30 bg-amber-400/5 px-3 py-2.5" data-testid="calendar-duplicates">
              <div className="text-xs text-amber-200 font-medium mb-1">{tx("Olası mükerrer etkinlikler — inceleyin")}</div>
              <p className="text-[0.7rem] text-amber-100/70 mb-2">{tx("Aynı başlık ve saate sahip birden çok kayıt bulundu. Otomatik silinmez; Google Takvim'den elle kaldırabilirsiniz.")}</p>
              {dupGroups.map((g, i) => (
                <div key={i} className="text-[0.72rem] text-white/75 mb-1">
                  <span className="text-white">{g[0].title}</span> · {fmt(g[0].start, g[0].all_day)} · <span className="text-amber-200">{tx("{n} kez", { n: g.length })}</span>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {(events?.events || []).map((e) => (
              <div key={e.event_id} className="bg-black/20 rounded-lg px-3 py-2.5 flex items-start justify-between gap-3" data-testid="calendar-event-row">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{e.title}</div>
                  <div className="label-eyebrow">{fmt(e.start, e.all_day)}{e.all_day ? ` · ${tx("tüm gün")}` : ` – ${fmt(e.end, false).split(" ").pop()}`}</div>
                  {e.location && <div className="text-[0.7rem] text-white/45 truncate">{e.location}</div>}
                </div>
                <Btn variant="ghost" onClick={() => setForm({
                  event_id: e.event_id, title: e.title, date: (e.start || "").slice(0, 10),
                  start_time: e.all_day ? "" : (e.start || "").slice(11, 16), end_time: e.all_day ? "" : (e.end || "").slice(11, 16),
                  all_day: e.all_day, attendeesText: (e.attendees || []).join(", "), notify: false, location: e.location || "", description: "",
                })} data-testid="calendar-edit-btn">{tx("Düzenle")}</Btn>
              </div>
            ))}
          </div>
        </div>
      )}

      {form && (
        <div className="panel p-4 sm:p-5 mb-4" data-testid="calendar-draft-form">
          <div className="label-eyebrow mb-3">{form.event_id ? tx("Etkinlik düzenleme taslağı") : tx("Yeni etkinlik taslağı")}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={tx("Başlık")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="calendar-title-input" />
            <Field label={tx("Tarih (YYYY-AA-GG)")} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="calendar-date-input" />
            {!form.all_day && <Field label={tx("Başlangıç (SS:DD)")} value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="calendar-start-input" />}
            {!form.all_day && <Field label={tx("Bitiş (SS:DD, boşsa 1 saat)")} value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />}
            <Field label={tx("Yer")} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <Field label={tx("Katılımcılar (virgülle)")} value={form.attendeesText} onChange={(e) => setForm({ ...form, attendeesText: e.target.value })} data-testid="calendar-attendees-input" />
          </div>
          <div className="flex items-center gap-4 flex-wrap mb-3">
            <label className="flex items-center gap-2 text-sm text-white/75">
              <input type="checkbox" checked={form.all_day} onChange={(e) => setForm({ ...form, all_day: e.target.checked })} data-testid="calendar-allday-toggle" />
              {tx("Tüm gün")}
            </label>
            <label className="flex items-center gap-2 text-sm text-white/75">
              <input type="checkbox" checked={form.notify} onChange={(e) => setForm({ ...form, notify: e.target.checked })} data-testid="calendar-notify-toggle" />
              {tx("Katılımcılara Google daveti gönder")}
            </label>
          </div>
          <div className="flex gap-2">
            <Btn onClick={saveDraft} data-testid="calendar-save-draft-btn">{tx("Taslağı kaydet")}</Btn>
            <Btn variant="ghost" onClick={() => setForm(null)}>{tx("Vazgeç")}</Btn>
          </div>
        </div>
      )}

      {drafts.length > 0 && (
        <div className="panel p-4 sm:p-5" data-testid="calendar-drafts">
          <div className="label-eyebrow mb-3">{tx("Taslaklar")}</div>
          <div className="space-y-2">
            {drafts.map((d) => {
              const done = d.status === "completed";
              return (
              <div key={d.draft_id} className={`bg-black/20 rounded-lg px-3 py-2.5 flex items-start justify-between gap-3 ${done ? "opacity-70" : ""}`} data-testid="calendar-draft-row">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{d.title || tx("(başlıksız)")}</div>
                  <div className="label-eyebrow">{d.date} {d.all_day ? `· ${tx("tüm gün")}` : `· ${d.start_time || tx("saat yok")}`} · {tx("sürüm {v}", { v: d.revision })}</div>
                  {d.needs_time && <Badge color="amber">{tx("saat gerekli")}</Badge>}
                  {done && <Badge color="green">{tx("takvime eklendi")}</Badge>}
                  {d.last_apply && <div className="text-[0.68rem] text-emerald-300/80">{tx("işlendi: {id}", { id: d.last_apply.event_id })}</div>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Btn variant="ghost" onClick={() => setForm({ ...d, attendeesText: (d.attendees || []).join(", ") })}>{tx("Düzenle")}</Btn>
                  {done
                    ? <Btn disabled data-testid="calendar-done-btn">{tx("Eklendi")}</Btn>
                    : <Btn onClick={() => openReview(d)} disabled={!canWrite || d.needs_time} data-testid="calendar-review-btn">{tx("İncele ve onayla")}</Btn>}
                  <Btn variant="danger" onClick={() => del(d.draft_id)} aria-label={tx("Sil")} data-testid="calendar-draft-delete-btn"><Trash2 size={13} /></Btn>
                </div>
              </div>
            ); })}
          </div>
          {!canWrite && <p className="text-[0.72rem] text-amber-200/90 mt-3">{tx("Takvime yazmak için yukarıdan Takvimi etkinleştirin.")}</p>}
        </div>
      )}

      {review && (
        <ReviewConfirm testid="calendar-review-modal" title={`${tx(review.review.action)} — ${tx("onay")}`}
          rows={[[tx("Taslak"), `${review.review.draft_id} · ${tx("sürüm {v}", { v: review.review.revision })}`],
            [tx("Başlık"), review.review.title],
            [tx("Takvim"), review.review.calendar_id],
            [tx("Başlangıç"), review.review.starts?.dateTime || review.review.starts?.date],
            [tx("Bitiş"), review.review.ends?.dateTime || review.review.ends?.date],
            [tx("Saat dilimi"), review.review.timezone],
            [tx("Tüm gün"), review.review.all_day ? tx("evet") : tx("hayır")],
            [tx("Yer"), review.review.location],
            [tx("Katılımcılar"), (review.review.attendees || []).join(", ")],
            [tx("Davet"), review.review.invites_will_be_sent ? tx("Google daveti GÖNDERİLECEK") : tx("gönderilmeyecek")]]}
          body={review.review.description || ""}
          warning={review.note}
          confirmLabel={review.review.action === "Yeni etkinlik" ? tx("Takvime ekle") : tx("Etkinliği güncelle")}
          onConfirm={apply} onCancel={() => setReview(null)} />
      )}
    </div>
  );
};

export default CalendarPanelReal;
